import React, { useEffect, useMemo, useState } from 'react';
import config from '../config';
import './UsersManager.css';

const EMPTY = { id: null, username: '', password: '', role: 'tech', fonction: '' };
const ROLES = {
  tech: { label: 'Technicien', detail: 'Son planning et ses chantiers', icon: '●' },
  admin: { label: 'Administrateur', detail: 'Planning et Kanban complets', icon: '◆' },
  superadmin: { label: 'Super administrateur', detail: 'Accès complet + gestion des utilisateurs', icon: '★' },
};

function formatDate(value) {
  if (!value) return 'Jamais';
  const date = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('fr-FR');
}

export default function UsersManager({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const selectedUser = users.find(user => Number(user.id) === Number(form.id));
  const protectedAccount = String(selectedUser?.username || '').toLowerCase() === 'admin';

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter(user =>
      [user.username, user.fonction, user.role].some(value => String(value || '').toLowerCase().includes(query))
    );
  }, [users, search]);

  async function request(payload) {
    const response = await fetch(`${config.apiBase}/cms_users_manager.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(config.tokenKey) || ''}`,
      },
      body: JSON.stringify(payload),
    });
    const text = await response.text();
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw new Error('Réponse invalide du serveur Isolacier.');
    }
    if (!response.ok || !data.success) throw new Error(data.message || `Erreur HTTP ${response.status}`);
    return data;
  }

  async function loadUsers() {
    setLoading(true);
    setError('');
    try {
      const data = await request({ action: 'list' });
      setUsers(Array.isArray(data.users) ? data.users : []);
    } catch (err) {
      setError(err.message || 'Chargement des utilisateurs impossible.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUsers();
  }, []);

  async function run(payload) {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const data = await request(payload);
      setUsers(Array.isArray(data.users) ? data.users : []);
      setMessage(data.message || 'Modification enregistrée.');
      return true;
    } catch (err) {
      setError(err.message || 'Opération impossible.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  function editUser(user) {
    setForm({
      id: Number(user.id),
      username: user.username || '',
      password: '',
      role: ROLES[user.role] ? user.role : 'tech',
      fonction: user.fonction || '',
    });
    setError('');
    setMessage('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function submit(event) {
    event.preventDefault();
    const done = await run({
      action: form.id ? 'update' : 'create',
      id: form.id,
      username: form.username.trim(),
      password: form.password,
      role: form.role,
      fonction: form.fonction.trim(),
    });
    if (done) setForm(EMPTY);
  }

  async function removeUser(user) {
    if (!window.confirm(`Supprimer définitivement le compte ${user.username} du CRM, du Planning et du Kanban ?`)) return;
    const done = await run({ action: 'delete', id: Number(user.id) });
    if (done && Number(form.id) === Number(user.id)) setForm(EMPTY);
  }

  const adminCount = users.filter(user => ['admin', 'superadmin'].includes(user.role)).length;

  return (
    <main className="crm-users-page">
      <header className="crm-users-hero">
        <div>
          <span>Administration Isolacier</span>
          <h1>Gestion des utilisateurs</h1>
          <p>Crée les comptes, attribue les rôles et synchronise automatiquement le Planning et le Kanban.</p>
        </div>
        <div className="crm-superadmin-chip">
          <small>Super administrateur connecté</small>
          <strong>{currentUser?.username || currentUser?.login}</strong>
        </div>
      </header>

      <section className="crm-users-stats">
        <article><strong>{users.length}</strong><span>comptes</span></article>
        <article><strong>{users.filter(user => user.role === 'tech').length}</strong><span>techniciens</span></article>
        <article><strong>{adminCount}</strong><span>administrateurs</span></article>
      </section>

      {message && <div className="crm-users-alert success">{message}</div>}
      {error && <div className="crm-users-alert error">{error}</div>}

      <div className="crm-users-layout">
        <section className="crm-users-panel crm-user-form-panel">
          <div className="crm-users-panel-title">
            <div>
              <span>{form.id ? `Compte #${form.id}` : 'Nouveau compte'}</span>
              <h2>{form.id ? 'Modifier le compte' : 'Créer un utilisateur'}</h2>
            </div>
            {form.id && <button type="button" onClick={() => setForm(EMPTY)}>Annuler</button>}
          </div>

          {protectedAccount && <div className="crm-protected-notice">Le compte superadmin principal est protégé.</div>}

          <form onSubmit={submit}>
            <label>
              Identifiant
              <input
                value={form.username}
                onChange={event => setForm({ ...form, username: event.target.value })}
                disabled={protectedAccount}
                placeholder="prenom.nom"
                required
              />
            </label>
            <label>
              {form.id ? 'Nouveau mot de passe' : 'Mot de passe initial'}
              <input
                type="password"
                minLength={8}
                value={form.password}
                onChange={event => setForm({ ...form, password: event.target.value })}
                placeholder={form.id ? 'Vide = inchangé' : '8 caractères minimum'}
                required={!form.id}
                autoComplete="new-password"
              />
            </label>
            <label>
              Fonction dans l’équipe
              <input
                value={form.fonction}
                onChange={event => setForm({ ...form, fonction: event.target.value })}
                placeholder="Technicien, chef d’équipe…"
              />
            </label>
            <label>
              Rôle
              <select
                value={form.role}
                onChange={event => setForm({ ...form, role: event.target.value })}
                disabled={protectedAccount}
              >
                {Object.entries(ROLES).map(([value, role]) => (
                  <option key={value} value={value}>{role.label} — {role.detail}</option>
                ))}
              </select>
            </label>
            <button className="crm-users-primary" type="submit" disabled={saving}>
              {saving ? 'Enregistrement…' : form.id ? 'Enregistrer les modifications' : 'Créer et synchroniser'}
            </button>
          </form>
        </section>

        <section className="crm-users-panel crm-users-list-panel">
          <div className="crm-users-toolbar">
            <div><span>Équipe Isolacier</span><h2>Comptes enregistrés</h2></div>
            <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Rechercher…" />
          </div>

          {loading ? <div className="crm-users-empty">Chargement…</div> : (
            <div className="crm-users-table-wrap">
              <table className="crm-users-table">
                <thead><tr><th>Utilisateur</th><th>Fonction</th><th>Rôle</th><th>Accès</th><th>Connexion</th><th>Actions</th></tr></thead>
                <tbody>
                  {filteredUsers.map(user => {
                    const role = ROLES[user.role] || ROLES.tech;
                    const elevated = ['admin', 'superadmin'].includes(user.role);
                    const synced = Number(user.synced_planning) && (!elevated || Number(user.synced_kanban));
                    const isProtected = String(user.username).toLowerCase() === 'admin';
                    return (
                      <tr key={user.id}>
                        <td><div className="crm-user-identity"><i>{String(user.username).charAt(0).toUpperCase()}</i><span><strong>{user.username}</strong><small>#{user.id}</small></span></div></td>
                        <td>{user.fonction || 'Non renseignée'}</td>
                        <td><span className={`crm-role role-${user.role}`}>{role.icon} {role.label}</span></td>
                        <td><div className="crm-access-tags"><span>Planning</span>{elevated ? <span>Kanban</span> : <em>Kanban bloqué</em>}{user.role === 'superadmin' && <b>Utilisateurs</b>}</div></td>
                        <td>{formatDate(user.last_login)}</td>
                        <td><div className="crm-user-actions">
                          {!synced && <button type="button" onClick={() => run({ action: 'sync', id: Number(user.id) })} disabled={saving}>Synchroniser</button>}
                          <button type="button" onClick={() => editUser(user)} disabled={saving}>Modifier</button>
                          {!isProtected && <button className="danger" type="button" onClick={() => removeUser(user)} disabled={saving}>Supprimer</button>}
                        </div></td>
                      </tr>
                    );
                  })}
                  {!filteredUsers.length && <tr><td colSpan="6" className="crm-users-empty">Aucun utilisateur trouvé.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
